<script setup lang="ts" generic="T extends Record<string, any>">
import { onMounted, ref, computed } from "vue";
import { watchDebounced } from "@vueuse/core";
import axios from "src/api/mutator/axios";
import { useToast } from "primevue/usetoast";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { FilterMatchMode } from "@primevue/core/api";
import SharedResizableDrawer from "./SharedResizableDrawer.vue";
import { entityProvider } from "src/composables/useEntityProvider";

interface Props<T> {
  data?: string | T | null;
  entity: string;
  entityLabel?: string | string[];
  entityValue?: string;
  labelSeparator?: string;
  returnEntity?: boolean;
  visible?: boolean;
  requests?: any;
  entityUrlParams?: Record<string, string | number>;
}

const props = withDefaults(defineProps<Props<T>>(), {
  entityLabel: "name",
  entityValue: "id",
  labelSeparator: " - ",
  returnEntity: false,
  entityUrlParams: () => ({}),
});

const visible = defineModel<boolean>("visible");
const toast = useToast();

const entities = ref<T[]>([]);
const loading = ref(false);
const totalRecords = ref(0);
const lazyState = ref({
  rows: 100,
  page: 1,
  search: "",
});

const globalFilterFields = ref<string[]>([]);

const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const emit = defineEmits<{
  "update:label": [value: string];
  "update:data": [value: string | T];
  "update:entity-data": [value: T];
  change: [value: string | T];
  loading: [value: boolean];
}>();

const columns = computed(() => {
  if (Array.isArray(props.entityLabel)) {
    return [
      ...props.entityLabel.map((field) => ({
        field,
        header: field.charAt(0).toUpperCase() + field.slice(1),
      })),
    ];
  }

  // If no entityLabel is provided and we have entities data
  if (props.entityLabel === "name" && entities.value.length > 0) {
    const firstEntity = entities.value[0];
    return Object.keys(firstEntity)
      .filter((key) => {
        const value = firstEntity[key];
        return (
          key !== "id" &&
          value !== null &&
          typeof value !== "object" &&
          !Array.isArray(value)
        );
      })
      .map((field) => ({
        field,
        header: field.charAt(0).toUpperCase() + field.slice(1),
      }));
  }

  return [
    {
      field: props.entityLabel as string,
      header: (props.entityLabel as string).charAt(0).toUpperCase() + (props.entityLabel as string).slice(1),
    },
  ];
});

function getEntityLabel(entity: Record<string, any>): string {
  if (Array.isArray(props.entityLabel)) {
    return props.entityLabel
      .map((field) => {
        const value = entity[field];
        return value != null ? String(value) : "";
      })
      .filter(Boolean)
      .join(props.labelSeparator);
  }

  // If no entityLabel is provided, join all fields except id and objects/arrays
  if (props.entityLabel === "name" && Object.keys(entity).length > 0) {
    return Object.entries(entity)
      .filter(([key, value]) => key !== "id" && value !== null && typeof value !== "object" && !Array.isArray(value))
      .map(([_, value]) => String(value))
      .filter(Boolean)
      .join(props.labelSeparator);
  }

  return String(entity[props.entityLabel] || entity[props.entityValue] || "");
}

async function loadEntities(state: { page: number; rows: number; search?: string }) {
  emit('loading', true);

  // Get search fields from entityLabel or columns
  const searchFields = Array.isArray(props.entityLabel)
    ? props.entityLabel
    : globalFilterFields.value;

  if (props.requests?.fetchAll) {
    try {
      const { mutateAsync } = props.requests.fetchAll();
      const params: any = { page: state.page, limit: state.rows };
      if (state.search) {
        params.search = state.search;
        params.searchFields = searchFields;
      }
      const response = await mutateAsync({ params, data: {} });
      entities.value = response.data;
      // Calculate total records from total_pages if total is not provided
      totalRecords.value = response.total || (response.total_pages * response.limit);
    } finally {
      emit('loading', false);
    }
    return;
  }

  try {
    // Use the entity provider instead of direct axios call
    const customFetch = props.requests?.fetchAll
      ? () => {
          const { mutateAsync } = props.requests!.fetchAll();
          const params: any = { page: state.page, limit: state.rows };
          if (state.search) {
            params.search = state.search;
            params.searchFields = searchFields;
          }
          return mutateAsync({ params, data: {} });
        }
      : undefined;

    const params: any = { page: state.page, limit: state.rows };
    if (state.search) {
      params.search = state.search;
      params.searchFields = searchFields;
    }

    const response = await entityProvider.loadEntities<T>(
      props.entity,
      params,
      props.entityUrlParams,
      customFetch
    );

    if (!response?.data) {
      return;
    }

    entities.value = response.data;
    totalRecords.value = response.total;
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to load entities",
      life: 3000,
    });
  } finally {
    emit('loading', false);
  }
}

function onPage(event: { first: number; rows: number; page: number }) {
  lazyState.value = { ...lazyState.value, ...event };
  loadEntities({ page: event.page + 1, rows: event.rows, search: lazyState.value.search });
}

function selectEntity(event: { data: T }) {
  const entity = event.data;
  const entityValue = entity[props.entityValue] as string;

  emit("update:data", props.returnEntity ? entity : entityValue);
  emit("update:label", getEntityLabel(entity));
  emit("update:entity-data", entity);
  emit("change", props.returnEntity ? entity : entityValue);
  visible.value = false;
}

onMounted(async () => {
  await loadEntities({ page: 1, rows: lazyState.value.rows, search: lazyState.value.search });
  globalFilterFields.value = columns.value?.map((col) => col.field) || [];
});

// Watch for search filter changes and trigger server-side search
watchDebounced(() => filters.value.global.value, (newSearch) => {
  lazyState.value.search = newSearch || "";
  lazyState.value.page = 1; // Reset to first page on new search
  loadEntities({ page: 1, rows: lazyState.value.rows, search: lazyState.value.search });
}, { debounce: 300 });
</script>

<template>
  <SharedResizableDrawer
    v-model:visible="visible"
    position="right"
    :header="entity"
  >
    <div class="p-0">
      <DataTable
        v-model:filters="filters"
        :value="entities"
        :loading="loading"
        :lazy="true"
        :total-records="totalRecords"
        :rows="100"
        paginator
        @page="onPage"
        :global-filter-fields="[]"
        filterDisplay="menu"
        :rows-per-page-options="[50, 100, 200]"
        selectionMode="single"
        @row-select="selectEntity"
        tableStyle="min-width: 100%"
        stripedRows
        hover
      >
        <template #header>
          <div class="flex justify-end">
            <IconField>
              <InputIcon>
                <i class="pi pi-search" />
              </InputIcon>
              <InputText v-model="filters['global'].value" placeholder="Search" />
            </IconField>
          </div>
        </template>
        <Column v-for="col in columns" :key="col.field" :field="col.field" :header="col.header" />
      </DataTable>
    </div>
  </SharedResizableDrawer>
</template>
